/**
 * Split-delivery-line sub-dialog (CR-6038).
 *
 * Lets the supplier break one imported delivery line's qty into N pieces so
 * each piece can be allocated to a different ASN. The sum of the splits must
 * equal the original quantity exactly — backend rejects mismatches, so this
 * dialog gates submission visually before the network call.
 */
import { useState, useEffect } from 'react';
import { Loader2, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { purchasingAPI } from '../../../lib/api';
import { toast } from 'sonner';
import { DeliveryLineOption } from './types';

interface SplitDeliveryLineDialogProps {
  line: DeliveryLineOption | null;
  onClose: () => void;
  onSplit: (originalId: number, newIds: number[]) => void;
}

export function SplitDeliveryLineDialog({ line, onClose, onSplit }: SplitDeliveryLineDialogProps) {
  const [splitInputs, setSplitInputs] = useState<string[]>(['', '']);
  const [splitting, setSplitting] = useState(false);

  useEffect(() => {
    if (!line) return;
    // Default to a 50/50 split rounded to 2dp (matches qty_released precision).
    const total = Number(line.qty_released) || 0;
    const half = (total / 2).toFixed(2);
    const other = (total - Number(half)).toFixed(2);
    setSplitInputs([half, other]);
  }, [line]);

  const updateInput = (idx: number, value: string) => {
    setSplitInputs((prev) => prev.map((v, i) => (i === idx ? value : v)));
  };

  const addRow = () => setSplitInputs((prev) => [...prev, '0']);

  const removeRow = (idx: number) =>
    setSplitInputs((prev) => (prev.length <= 2 ? prev : prev.filter((_, i) => i !== idx)));

  const total = line ? Number(line.qty_released) : 0;
  const sum = splitInputs.reduce((acc, s) => acc + (Number(s) || 0), 0);
  const valid =
    splitInputs.length >= 2 &&
    splitInputs.every((s) => Number(s) > 0) &&
    Math.abs(sum - total) < 0.005;

  const submit = async () => {
    if (!line) return;
    const numericSplits = splitInputs.map((s) => Number(s));
    if (numericSplits.some((n) => !Number.isFinite(n) || n <= 0)) {
      toast.error('Each split must be a positive number.');
      return;
    }
    setSplitting(true);
    try {
      const response = await purchasingAPI.splitDeliveryLine(line.id, numericSplits);
      toast.success(`Line split into ${response.data.split_ids.length} parts.`);
      onSplit(line.id, response.data.split_ids);
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string } } };
      toast.error(axiosErr?.response?.data?.error || 'Split failed.');
    } finally {
      setSplitting(false);
    }
  };

  return (
    <Dialog open={line != null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Split Delivery Line</DialogTitle>
          <DialogDescription>
            {line && (
              <>
                Split <span className="font-mono">{line.delivery_number}</span> (qty{' '}
                <span className="font-medium">{total}</span>) into multiple shipments. Each
                portion becomes its own delivery line you can allocate separately.
              </>
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 py-2">
          {splitInputs.map((val, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground w-16">Part {idx + 1}</span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={val}
                onChange={(e) => updateInput(idx, e.target.value)}
                className="flex-1 px-2 py-1 border rounded text-sm"
              />
              {splitInputs.length > 2 && (
                <button
                  onClick={() => removeRow(idx)}
                  className="p-1 hover:bg-muted rounded text-muted-foreground"
                  aria-label={`Remove part ${idx + 1}`}
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={addRow} className="text-xs">
            + Add another split
          </Button>
          <div
            className={`text-xs px-2 py-1 rounded ${
              valid ? 'bg-green-50 text-green-800' : 'bg-amber-50 text-amber-800'
            }`}
          >
            Sum: {sum.toFixed(2)} / Required: {total}
            {!valid && ' — adjust so the splits add up exactly.'}
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose} disabled={splitting}>
            Cancel
          </Button>
          <Button size="sm" onClick={submit} disabled={splitting || !valid}>
            {splitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
            Split Line
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
